import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Method, Prop } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * Para uma descrição detalhada, consulte a [documentação do GovBR-DS](https://www.gov.br/ds/components/modal?tab=designer).
 *
 * @slot header - Cabeçalho do modal. Se não for usado, o `title-text` será exibido.
 * @slot default - Corpo do modal.
 * @slot footer - Rodapé do modal.
 * @slot close-button - Botão de fechar customizado. Se não for usado, o botão padrão será exibido.
 *
 */
@Component({
  tag: 'br-modal',
  styleUrl: 'modal.scss',
  shadow: true,
  formAssociated: true,
})
export class Modal {
  /** Referência ao elemento host do componente. */
  @Element() el: HTMLBrModalElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('modal')

  /** O texto do título a ser exibido no cabeçalho do modal. Usado quando o slot `header` não é fornecido. */
  @Prop() readonly titleText: string

  /** Controla a visibilidade do modal. */
  @Prop({ reflect: true, mutable: true }) show = false

  /** Se `true`, o modal se fechará automaticamente ao clicar no botão de fechar ou pressionar a tecla 'Escape'. */
  @Prop({ reflect: true }) autoClose = false

  /** Seletor CSS para o elemento que deve receber foco quando o modal é aberto. */
  @Prop() readonly initialFocusSelector: string

  /**
   * Define o tamanho (largura) do modal.
   * Valores possíveis: 'xsmall', 'small', 'medium', 'large', 'auto'.
   */
  @Prop({ reflect: true }) readonly size: 'xsmall' | 'small' | 'medium' | 'large' | 'auto' = 'medium'

  /** Se `true`, exibe um fundo escurecido (scrim) atrás do modal. */
  @Prop({ reflect: true }) readonly scrim = false

  /** Se `true`, habilita a rolagem interna do conteúdo do modal. */
  @Prop({ reflect: true }) readonly scrollable = false

  /**
   * Define o alinhamento do conteúdo do rodapé (slot="footer").
   * Valores possíveis: 'start', 'end', 'center'.
   */
  @Prop({ reflect: true }) readonly alignFooter: 'start' | 'end' | 'center' = 'center'

  /** Evento emitido quando o modal solicita ser fechado (pelo botão 'x', tecla 'Escape' ou clique no scrim). */
  @Event() brModalClose: EventEmitter<void>

  private get titleId() {
    return `${this.customId}-title`
  }

  private get contentId() {
    return `${this.customId}-content`
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-modal': true,
      [this.size]: true,
      scrollable: this.scrollable,
    }
  }

  private handleClose = () => {
    this.brModalClose.emit()
    if (this.autoClose) {
      this.show = false

      // Se o modal está dentro de um br-scrim, fecha o scrim também
      const parentScrim = this.el.closest('br-scrim')
      if (parentScrim) {
        parentScrim.close()
      }
    }
  }

  /**
   * Método público para abrir o modal
   */
  @Method()
  async open() {
    this.show = true
  }

  /**
   * Método público para fechar o modal
   */
  @Method()
  async close() {
    this.show = false

    // Se o modal está dentro de um br-scrim, fecha o scrim também
    const parentScrim = this.el.closest('br-scrim')
    if (parentScrim) {
      parentScrim.close()
    }
  }

  render() {
    if (!this.show) {
      return null
    }

    return (
      <Host>
        <div
          class={this.getCssClassMap()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={this.titleId}
          aria-describedby={this.contentId}
        >
          <div class="br-modal-header">
            <div class="modal-title" id={this.titleId}>
              <slot name="header">{this.titleText}</slot>
            </div>
            <slot name="close-button">
              <br-button class="close" shape="circle" density="small" aria-label="Fechar" onClick={this.handleClose}>
                <br-icon icon-name="fa-solid:times"></br-icon>
              </br-button>
            </slot>
          </div>
          <div id={this.contentId} class="br-modal-body">
            <slot></slot>
          </div>
          <div class={`br-modal-footer ${this.alignFooter ? `justify-content-${this.alignFooter}` : ''}`}>
            <slot name="footer"></slot>
          </div>
        </div>
      </Host>
    )
  }
}
